/**
 * Lazy DOM overlay for the action tracer.
 *
 * Built as a structured set of `<div>` rows rather than a single
 * `<pre>` so:
 *
 * - long labels wrap cleanly instead of overflowing.
 * - the window can be made draggable and resizable via
 *   {@link makeMovableResizable} without breaking text layout.
 *
 * Only "completed" trace rows are rendered. An earlier version also
 * showed an "in-flight" row at T0, but because the main thread is blocked
 * synchronously until after T2 (Redux done) the browser couldn't paint
 * that row until the action was effectively complete, making it
 * misleading rather than useful.
 */

import { makeMovableResizable } from './overlay-window.ts';

const OVERLAY_ID = 'action-trace-debug';
const HEADER_ID = 'action-trace-debug-header';
const BODY_ID = 'action-trace-debug-body';
const STORAGE_KEY_RECT = 'databases-perf-tracing.action-overlay-rect';
const MAX_ROWS = 10;

export type TraceStatus = 'complete' | 'no-yjs';

export interface OverlayRow {
  id: string;
  label: string;
  status: TraceStatus;
  /** Duration from action start to first Yjs commit, ms. `null` if not reached. */
  actionToYjsMs: number | null;
  /** Duration from Yjs commit to Redux sync, ms. `null` if not reached. */
  yjsToReduxMs: number | null;
  /** Duration from Redux sync to next browser paint, ms. `null` if not reached. */
  reduxToPaintMs: number | null;
  /** End-to-end duration from action start to next paint, ms. `null` if not reached. */
  totalMs: number | null;
  /** Number of Yjs transactions observed for this trace. */
  transactionCount: number;
}

const completedRows: OverlayRow[] = [];

function getOrCreateOverlay(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) {
    return overlay;
  }

  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  Object.assign(overlay.style, {
    position: 'fixed',
    top: '70px',
    right: '10px',
    zIndex: '99999',
    background: 'rgba(0,0,0,0.88)',
    color: '#0f0',
    padding: '12px 16px',
    borderRadius: '8px',
    fontFamily: 'monospace',
    fontSize: '12px',
    lineHeight: '1.45',
    width: '440px',
    maxWidth: 'calc(100vw - 40px)',
    maxHeight: 'calc(100vh - 100px)',
    overflow: 'auto',
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
    pointerEvents: 'auto'
  } satisfies Partial<CSSStyleDeclaration>);

  const body = document.createElement('div');
  body.id = BODY_ID;
  body.setAttribute(
    'style',
    'display:flex; flex-direction:column; gap:6px; word-break:break-word;'
  );
  overlay.appendChild(body);

  document.body.appendChild(overlay);

  // Drag + resize. We delay this until after the element is in the DOM so
  // getBoundingClientRect inside the helper returns sensible numbers when
  // restoring persisted position.
  const handle = makeMovableResizable(overlay, {
    storageKey: STORAGE_KEY_RECT,
    resizable: true,
    title: 'ACTION TRACE — drag / resize'
  }).getHandle();
  handle.id = HEADER_ID;

  // Add a clear (✕) button on the right side of the drag bar. Stops
  // pointerdown propagation so clicking it doesn't start a drag.
  const clearButton = document.createElement('button');
  clearButton.type = 'button';
  clearButton.setAttribute('aria-label', 'Clear traces');
  clearButton.setAttribute('title', 'Clear traces');
  clearButton.textContent = '✕';
  Object.assign(clearButton.style, {
    cursor: 'pointer',
    background: 'transparent',
    border: '0',
    color: '#f88',
    fontSize: '14px',
    lineHeight: '1',
    padding: '0 4px',
    margin: '0 0 0 8px',
    fontFamily: 'monospace'
  } satisfies Partial<CSSStyleDeclaration>);
  clearButton.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
  });
  clearButton.addEventListener('click', (e) => {
    e.stopPropagation();
    clearOverlay();
  });
  handle.appendChild(clearButton);

  return overlay;
}

function formatMs(value: number | null): string {
  if (value === null) {
    return '—';
  }
  // Sub-millisecond performance.now() jitter can produce small negative
  // diffs between phase markers; clamp to zero for display rather than
  // showing a misleading negative number. (Underlying T0..T3 raw values
  // are still preserved on the trace for any downstream consumer.)
  const clamped = value < 0 ? 0 : value;
  return `${clamped.toFixed(1)}`;
}

function statusIcon(status: TraceStatus): string {
  switch (status) {
    case 'complete':
      return '🟢';
    case 'no-yjs':
      return '⚪';
  }
}

function renderRow(row: OverlayRow): HTMLElement {
  const container = document.createElement('div');
  container.setAttribute(
    'style',
    'display:flex; flex-direction:column; gap:2px; padding:4px 6px; ' +
      'background:rgba(255,255,255,0.04); border-radius:4px; ' +
      'border-left:3px solid ' +
      (row.status === 'complete' ? '#0f0' : '#666') +
      ';'
  );

  const title = document.createElement('div');
  title.setAttribute('style', 'display:flex; gap:6px; align-items:baseline;');
  title.innerHTML = `<span>${statusIcon(
    row.status
  )}</span><span style="color:#fff; word-break:break-word;">${escapeHtml(
    row.label
  )}</span>`;
  container.appendChild(title);

  if (row.status === 'no-yjs') {
    const meta = document.createElement('div');
    meta.setAttribute('style', 'color:#888; font-size:11px;');
    meta.textContent = '(no yjs commit observed)';
    container.appendChild(meta);
    return container;
  }

  const stats = document.createElement('div');
  stats.setAttribute(
    'style',
    'display:flex; flex-wrap:wrap; gap:6px 12px; font-size:11px;'
  );
  stats.appendChild(stat('action→yjs', formatMs(row.actionToYjsMs)));
  stats.appendChild(stat('yjs→redux', formatMs(row.yjsToReduxMs)));
  stats.appendChild(stat('redux→paint', formatMs(row.reduxToPaintMs)));
  stats.appendChild(stat('total', formatMs(row.totalMs), { highlight: true }));
  stats.appendChild(stat('tx', String(row.transactionCount)));
  container.appendChild(stats);

  return container;
}

function stat(label: string, value: string, opts?: { highlight?: boolean }): HTMLElement {
  const span = document.createElement('span');
  const color = opts?.highlight ? '#0f0' : '#9ce';
  span.innerHTML = `<span style="color:#888;">${label}:</span> <span style="color:${color};">${escapeHtml(
    value
  )}</span>`;
  return span;
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function render(): void {
  const overlay = getOrCreateOverlay();
  if (!overlay) {
    return;
  }
  const body = overlay.querySelector(`#${BODY_ID}`) as HTMLElement | null;
  if (!body) {
    return;
  }
  body.innerHTML = '';
  for (const row of completedRows) {
    body.appendChild(renderRow(row));
  }
}

export function showOverlayComplete(row: OverlayRow): void {
  completedRows.unshift(row);
  if (completedRows.length > MAX_ROWS) {
    completedRows.length = MAX_ROWS;
  }
  render();
}

export function hideOverlay(): void {
  completedRows.length = 0;
  if (typeof document === 'undefined') {
    return;
  }
  const overlay = document.getElementById(OVERLAY_ID);
  overlay?.remove();
}

/**
 * Empties the visible trace rows but keeps the overlay window mounted (and
 * keeps the persisted position / size). Triggered by the ✕ button on the
 * drag bar.
 */
export function clearOverlay(): void {
  completedRows.length = 0;
  render();
}
